import { Button } from "@rosc/ui/v32/button";
import { Notice } from "@rosc/ui/v32/notice";
import { LifeBuoy, Plus } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { StatusFile } from "@/common/enums/enums";
import { useFeedbackStore } from "@/store/feedback.store";

export const isErrorVisit = (status: string | null | undefined): boolean =>
  status === StatusFile.ERROR.toString();

// A visit whose processing ended in "Error" (the list's red Error badge):
// what happened, what it means for the note on screen, and what to do. The
// API gives no reason, so the copy stays general. Actions: record the
// missing part again (Add recording, when available) or contact support
// through the in-app feedback dialog, with this visit attached.
export const VisitErrorNotice = ({
  status,
  visitPublicId,
  onAddRecording,
}: {
  status: string | null | undefined;
  visitPublicId: string | null | undefined;
  onAddRecording?: () => void;
}): JSX.Element | null => {
  const { t } = useTranslation();
  if (!isErrorVisit(status)) return null;
  return (
    <Notice
      actions={
        <>
          {onAddRecording && (
            <Button
              className="w-full sm:w-auto"
              data-testid="visit-error-add-recording"
              variant="secondary"
              onClick={onAddRecording}
            >
              <Plus aria-hidden />
              {t("visit.errorState.addRecording")}
            </Button>
          )}
          <Button
            className="w-full sm:w-auto"
            data-testid="visit-error-support"
            variant={onAddRecording ? "ghost" : "secondary"}
            onClick={() =>
              useFeedbackStore
                .getState()
                .show({ visitPublicId: visitPublicId ?? null, type: "bug" })
            }
          >
            <LifeBuoy aria-hidden />
            {t("visit.errorState.contactSupport")}
          </Button>
        </>
      }
      description={t("visit.errorState.description")}
      testId="visit-error-notice"
      title={t("visit.errorState.title")}
      tone="error"
    />
  );
};
